import { Flex, HStack, Tag, TagLabel, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomFieldValue } from '../../../types';

type CrossCoverageAllApptsBadgeProps = {
  customField?: CustomFieldValue[];
};

const CrossCoverageAllApptsBadge: React.FC<
  CrossCoverageAllApptsBadgeProps
> = ({ customField }) => {
  const navigate = useNavigate();

  // First: Early exit if empty
  if (!Array.isArray(customField) || customField.length === 0) {
    return null;
  }

  const field = customField[0];
  const providers = field.metadata?.providers as
    | { providerId: string; providerLabel: string }[]
    | undefined;
  const whoProvidesCoverage = field.metadata?.whoProvidesCoverage as
    | string
    | undefined;
  const whatAppointments = field.metadata?.whatAppointments as
    | string
    | undefined;

  // If no value or metadata, show None
  if (!field.value || field.value === 'None') {
    return (
      <Text fontSize="0.875rem" fontWeight="700" color="text.primary" lineHeight="1.25rem">
        None
      </Text>
    );
  }

  // Show coverage information with providers and details
  return (
    <Flex direction="column" gap="0.25rem">
      {/* Main coverage value */}
      <Text fontSize="0.875rem" fontWeight="700" color="blue.700" lineHeight="1.25rem">
        {field.value}
      </Text>

      {/* Provider tags when available */}
      {providers && providers.length > 0 && (
        <HStack spacing={1} flexWrap="wrap" mt={1}>
          {providers.map((p) => (
            <Tag
              as="button"
              key={p.providerId}
              size="sm"
              colorScheme="blue"
              borderRadius="full"
              variant="outline"
              cursor="pointer"
              onClick={() => navigate(`/providers/${p.providerId}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/providers/${p.providerId}`);
                }
              }}
              _hover={{ bg: 'blue.50', borderColor: 'blue.400', transform: 'translateY(-1px)' }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.15s ease-in-out"
              fontSize="0.75rem"
              fontWeight="500"
              tabIndex={0}
            >
              <TagLabel>{p.providerLabel}</TagLabel>
            </Tag>
          ))}
        </HStack>
      )}

      {/* Additional details for who provides coverage and what appointments */}
      {(whoProvidesCoverage || whatAppointments) && (
        <VStack align="start" spacing={0.5} mt={1}>
          {whoProvidesCoverage && (
            <Text
              fontSize="0.75rem"
              color="gray.600"
              fontStyle="italic"
            >
              Who: {whoProvidesCoverage}
            </Text>
          )}
          {whatAppointments && (
            <Text
              fontSize="0.75rem"
              color="gray.600"
              fontStyle="italic"
            >
              What: {whatAppointments}
            </Text>
          )}
        </VStack>
      )}
    </Flex>
  );
};

export default CrossCoverageAllApptsBadge;